/**
 * AppShell — la structure de la console (mandat §5-6).
 *
 * Navigation en six blocs, une QUESTION par bloc (voir UX_UI_REDESIGN_PLAN.md). La sidebar
 * porte la charte : bleu nuit, texte blanc, actif marqué par la barre vert. La palette
 * `Ctrl+K`, le basculement thème NOC et le bandeau hors ligne (UI_STATES #6) vivent dans la
 * barre supérieure — ils sont globaux, pas des pages.
 *
 * Responsive : sous le point de rupture, la sidebar passe hors canevas avec voile — une
 * vraie adaptation, pas un desktop rétréci.
 */
import { useCallback, useEffect, useState, type ReactNode } from "react";
import { Link, useLocation } from "react-router-dom";
import { clearToken, getUserScope } from "@/api/client";
import { listMissionDeltas, purgeOfflineScope } from "@/offline/db";
import { CommandPalette } from "./ui/CommandPalette";
import {
  IconAdmin,
  IconAlerte,
  IconCarte,
  IconDirection,
  IconEditer,
  IconImport,
  IconIncident,
  IconIntelligence,
  IconMenu,
  IconMission,
  IconQualite,
  IconRapports,
  IconRecherche,
  IconServices,
  IconTopologie,
  IconCapacite,
  IconAccueil,
  IconEnergie,
  IconSite,
} from "./ui/icons";
import type { ComponentType } from "react";

interface Entree {
  label: string;
  path: string;
  Icone: ComponentType<{ size?: number }>;
}

const BLOCS: { titre: string; entrees: Entree[] }[] = [
  {
    titre: "Accueil",
    entrees: [
      { label: "Centre de commandement", path: "/", Icone: IconAccueil },
      { label: "Vue NOC", path: "/noc", Icone: IconAlerte },
      { label: "Carte d'urgence", path: "/urgence", Icone: IconEnergie },
    ],
  },
  {
    // Le patrimoine : ce que l'organisation possède, avant ce qu'elle en fait. Dix-huit
    // domaines ne tiennent pas dans une barre latérale sans la rendre illisible ; les deux du
    // quotidien restent ici, le sommaire mène aux autres.
    titre: "Patrimoine",
    entrees: [
      { label: "Sites", path: "/inventaire/sites", Icone: IconSite },
      { label: "Équipements", path: "/inventaire/actifs", Icone: IconCapacite },
      { label: "Tous les inventaires", path: "/inventaire", Icone: IconRapports },
    ],
  },
  {
    titre: "Réseau",
    entrees: [
      { label: "Carte SIG", path: "/carte-v2", Icone: IconCarte },
      { label: "Topologie", path: "/reseau", Icone: IconTopologie },
      { label: "Câbles optiques", path: "/inventaire/cables", Icone: IconTopologie },
      { label: "Liens loués", path: "/inventaire/liens-loues", Icone: IconServices },
      { label: "Liaisons FH", path: "/inventaire/liaisons-fh", Icone: IconTopologie },
      { label: "Capacité", path: "/capacite", Icone: IconCapacite },
    ],
  },
  {
    titre: "Services",
    entrees: [
      { label: "Carte des services", path: "/carte-services", Icone: IconServices },
      { label: "Services livrés", path: "/inventaire/services", Icone: IconServices },
    ],
  },
  {
    titre: "Opérations",
    entrees: [
      { label: "Incidents", path: "/incidents", Icone: IconIncident },
      { label: "Ordres de travail", path: "/inventaire/ordres-de-travail", Icone: IconMission },
      { label: "Missions", path: "/mission", Icone: IconMission },
      { label: "Qualité des données", path: "/qualite-donnees", Icone: IconQualite },
    ],
  },
  {
    titre: "Analyse",
    entrees: [
      { label: "Intelligence", path: "/intelligence", Icone: IconIntelligence },
      { label: "Simulateur d'impact", path: "/simulation-impact", Icone: IconAlerte },
      { label: "Direction", path: "/direction", Icone: IconDirection },
      { label: "Rapports", path: "/rapports", Icone: IconRapports },
    ],
  },
  {
    titre: "Administrer",
    entrees: [
      { label: "Importer des données", path: "/import", Icone: IconImport },
      { label: "Éditer une géométrie", path: "/editer", Icone: IconEditer },
      // `/admin` appartient au frontal, qui le renvoie à Django : la console ne peut pas
      // le servir, et cette entrée tombait sur un 404 du backend.
      { label: "Administration", path: "/administration", Icone: IconAdmin },
    ],
  },
];

const CLE_THEME = "infranet-theme";

function themeInitial(): "jour" | "noc" {
  const memorise = localStorage.getItem(CLE_THEME);
  if (memorise === "noc" || memorise === "jour") return memorise;
  return "jour";
}

export default function Layout({ children }: { children: ReactNode }) {
  const location = useLocation();
  const [sidebarOuverte, setSidebarOuverte] = useState(false); // mobile : hors canevas
  const [repliee, setRepliee] = useState(
    () => localStorage.getItem("infranet-sidebar") === "reduite",
  );
  const [paletteOuverte, setPaletteOuverte] = useState(false);
  const [theme, setTheme] = useState<"jour" | "noc">(themeInitial);
  const [horsLigne, setHorsLigne] = useState(!navigator.onLine);
  // UI_STATES #6 : le bandeau dit COMBIEN attend la synchro, pas seulement « hors ligne ».
  const [enAttente, setEnAttente] = useState<number | null>(null);
  const [logoutError, setLogoutError] = useState<string | null>(null);

  // Le thème NOC est un attribut au racine : TOUT l'écran suit les jetons, y compris les
  // pages pas encore refondues (les classes Tailwind pontées lisent les mêmes variables).
  useEffect(() => {
    if (theme === "noc") {
      document.documentElement.dataset.theme = "noc";
    } else {
      delete document.documentElement.dataset.theme;
    }
    localStorage.setItem(CLE_THEME, theme);
  }, [theme]);

  useEffect(() => {
    const maj = () => setHorsLigne(!navigator.onLine);
    window.addEventListener("online", maj);
    window.addEventListener("offline", maj);
    // Compteur de deltas en attente : l'utilisateur terrain sait ce que sa prochaine
    // connexion va envoyer — il n'appuie pas sur « synchroniser » à l'aveugle.
    const compter = async () => {
      const scope = getUserScope();
      if (!scope) return;
      try {
        const deltas = await listMissionDeltas(scope);
        setEnAttente(deltas.length);
      } catch {
        setEnAttente(null); // stockage chiffré indisponible : ne pas alarmer
      }
    };
    void compter();
    window.addEventListener("infranet:deltas", compter as EventListener);
    window.addEventListener("online", compter as EventListener);
    return () => {
      window.removeEventListener("online", maj);
      window.removeEventListener("offline", maj);
      window.removeEventListener("infranet:deltas", compter as EventListener);
      window.removeEventListener("online", compter as EventListener);
    };
  }, []);

  // Palette : Ctrl+K / Cmd+K partout, Échap pour fermer.
  useEffect(() => {
    const surTouche = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setPaletteOuverte((o) => !o);
      } else if (e.key === "Escape") {
        setPaletteOuverte(false);
      }
    };
    window.addEventListener("keydown", surTouche);
    return () => window.removeEventListener("keydown", surTouche);
  }, []);

  // Fermer la sidebar mobile à chaque navigation : le contenu redevient visible.
  useEffect(() => {
    setSidebarOuverte(false);
  }, [location.pathname]);

  useEffect(() => {
    localStorage.setItem("infranet-sidebar", repliee ? "reduite" : "etendue");
  }, [repliee]);

  const handleLogout = useCallback(async () => {
    const scope = getUserScope();
    setLogoutError(null);
    try {
      if (scope) {
        await purgeOfflineScope(scope);
      }
      clearToken();
      window.location.href = "/login";
    } catch {
      setLogoutError(
        "Déconnexion annulée : les données terrain locales n'ont pas pu être effacées.",
      );
    }
  }, []);

  return (
    <div className="min-h-screen bg-surface-2 text-texte">
      <a
        href="#contenu-principal"
        className="sr-only focus:not-sr-only focus:absolute focus:left-2 focus:top-2 focus:z-50 focus:rounded focus:bg-surface focus:px-3 focus:py-2 focus:text-sm focus:shadow-e3"
      >
        Aller au contenu principal
      </a>

      {/* Barre supérieure */}
      <header className="sticky top-0 z-40 flex h-14 items-center gap-3 border-b border-nuit-700 bg-nuit px-3 text-white">
        <button
          type="button"
          onClick={() => setSidebarOuverte((o) => !o)}
          className="rounded p-2 hover:bg-nuit-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-tech lg:hidden"
          aria-label="Afficher ou masquer la navigation"
          aria-expanded={sidebarOuverte}
        >
          <IconMenu />
        </button>
        <Link to="/" className="flex items-center gap-2 font-bold tracking-wide">
          <span className="grid h-7 w-7 place-items-center rounded bg-vert-700 text-xs font-black text-white">
            IN
          </span>
          InfraNet GN
        </Link>

        <button
          type="button"
          onClick={() => setPaletteOuverte(true)}
          className="ml-auto hidden min-w-72 items-center gap-2 rounded border border-nuit-700 bg-nuit-900/60 px-3 py-1.5 text-sm text-white/70 hover:border-tech focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-tech sm:flex"
          aria-label="Ouvrir la recherche globale"
        >
          <IconRecherche size={16} />
          <span className="flex-1 text-left">Rechercher…</span>
          <kbd className="rounded bg-nuit-700 px-1.5 py-0.5 font-mono text-[10px]">Ctrl K</kbd>
        </button>
        <button
          type="button"
          onClick={() => setPaletteOuverte(true)}
          aria-label="Rechercher"
          className="ml-auto rounded p-2 hover:bg-nuit-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-tech sm:hidden"
        >
          <IconRecherche />
        </button>

        <button
          type="button"
          onClick={() => setTheme((t) => (t === "noc" ? "jour" : "noc"))}
          className="rounded px-2.5 py-1.5 text-xs font-semibold text-white/80 hover:bg-nuit-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-tech"
          aria-pressed={theme === "noc"}
          title="Basculer le mode NOC (sombre)"
        >
          {theme === "noc" ? "☀ Jour" : "☾ NOC"}
        </button>

        <button
          type="button"
          onClick={() => void handleLogout()}
          className="rounded px-2.5 py-1.5 text-sm font-semibold text-white/90 hover:bg-nuit-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-tech"
        >
          Déconnexion
        </button>
        {logoutError && (
          <span
            role="alert"
            className="absolute right-3 top-14 rounded bg-etat-down-fond px-3 py-1.5 text-xs text-etat-down-texte shadow-e2"
          >
            {logoutError}
          </span>
        )}
      </header>

      {/* Bandeau hors ligne (UI_STATES #6) : permanent quand le réseau tombe */}
      {horsLigne && (
        <div
          role="status"
          className="bg-etat-degrade-fond px-4 py-1.5 text-center text-sm font-semibold text-etat-degrade-texte"
        >
          Hors ligne — les saisies terrain sont conservées localement et partiront à la
          reconnexion.
          {enAttente !== null && enAttente > 0 && ` ${enAttente} élément(s) en attente.`}
        </div>
      )}

      <div className="flex">
        {/* Sidebar : off-canvas sous lg, fixe au-dessus */}
        {sidebarOuverte && (
          <div
            className="fixed inset-0 z-30 bg-nuit/50 lg:hidden"
            onClick={() => setSidebarOuverte(false)}
            aria-hidden="true"
          />
        )}
        <nav
          aria-label="Navigation principale"
          className={`fixed inset-y-0 left-0 z-30 mt-14 w-64 overflow-y-auto border-r border-nuit-700 bg-nuit pb-8 text-white transition-transform lg:sticky lg:top-14 lg:z-0 lg:mt-0 lg:h-[calc(100vh-3.5rem)] lg:translate-x-0 ${
            sidebarOuverte ? "translate-x-0" : "-translate-x-full"
          }`}
        >
          <button
            type="button"
            onClick={() => setRepliee((r) => !r)}
            className="mt-2 hidden w-full rounded px-4 py-2 text-left text-xs font-semibold text-white/50 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-tech lg:block"
            aria-pressed={repliee}
            title={repliee ? "Déplier la navigation" : "Replier la navigation"}
          >
            {repliee ? "»" : "« Replier"}
          </button>
          {BLOCS.map((bloc) => (
            <div key={bloc.titre} className="mt-4">
              <p className={`px-4 pb-1 text-[11px] font-bold uppercase tracking-widest text-white/40 ${repliee ? "sr-only" : ""}`}>
                {bloc.titre}
              </p>
              {bloc.entrees.map(({ label, path, Icone }) => {
                const actif = location.pathname === path;
                return (
                  <Link
                    key={path}
                    to={path}
                    className={`relative flex min-h-confort items-center gap-3 px-4 py-2 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-tech ${
                      actif
                        ? "bg-nuit-700 text-white"
                        : "text-white/70 hover:bg-nuit-900/60 hover:text-white"
                    } ${repliee ? "justify-center px-0" : ""}`}
                    aria-current={actif ? "page" : undefined}
                  >
                    {/* L'actif porte une BARRE, pas seulement une teinte (jamais la couleur seule) */}
                    {actif && (
                      <span
                        aria-hidden="true"
                        className="absolute left-0 top-0 h-full w-1 bg-vert-700"
                      />
                    )}
                    <Icone size={18} />
                    {label}
                  </Link>
                );
              })}
            </div>
          ))}
        </nav>

        <main id="contenu-principal" className="min-w-0 flex-1 p-4 lg:p-6">
          {children}
        </main>
      </div>

      <CommandPalette ouvert={paletteOuverte} fermer={() => setPaletteOuverte(false)} />
    </div>
  );
}
